<template>
  <div
    :key="getFieldID(schema)"
    class="invoice-item"
    >
    <div class="description">
      <magic-markdown
        :source="invoiceItemOptions.description"
        :image="true"
        :html="false" />
      <div class="price">
        <strong>
          {{ priceFmt(invoiceItemOptions.unitPrice, priceFmtOpts) }}
        </strong>
        per
        {{ invoiceItemOptions.unitsName || 'item' }}
      </div>
      <div class="times" v-if="!useSwitchForUnits">
        <font-awesome-icon :icon="faIcons.faTimes" />
      </div>
      <div class="units">
        <template
          v-if="schema.attributes.editing &&
            !disabledForAdminOnly &&
            !useSwitchForUnits">
          <select
            class="form-select unitsValue"
            v-model="value"
            :disabled="disabled || null"
            :nameId="schema.inputName"
            :id="getFieldID(schema)"
            :class="schema.fieldClasses"
          >
            <option
              v-for="u in units"
              :value="u"
              :key="u"
            >
              {{ u }}
            </option>
          </select>
        </template>
        <template
          v-else-if="schema.attributes.editing &&
            !disabledForAdminOnly &&
            useSwitchForUnits">
          <label class="form-switch">
            <input
              type="checkbox"
              :disabled="disabled || null"
              :nameId="schema.inputName"
              :id="getFieldID(schema)"
              :true-value="1"
              :false-value="0"
              v-model="value" />
            <i class="form-icon"></i> Add
          </label>
        </template>
        <template v-else>
          <span v-if="!lodash.isNull(value)" class="response unitsValue">
            {{ value }}
          </span>
          <span
            v-else-if="!schema.attributes.editing"
            style="font-style: italic; color: #555">
            Not available
          </span>
        </template>
        <div
          class="form-input-hint text-error"
          v-if="schema.attributes.editing && disabledForAdminOnly">
          Restricted to administrators.
        </div>
      </div>
    </div>
    <div class="image">
      <magic-markdown
        :source="invoiceItemOptions.imageArea"
        :image="true"
        :html="false" />
    </div>
  </div>
</template>

<script>
// This is a lightly modified version of vue-form-generator's select field.
import _ from 'lodash';

import MagicMarkdown from 'components/Widgets/MagicMarkdown';
import { MixinDataToReactiveData } from 'assets/js/util';

import { abstractField } from 'components/Widgets/FormGenerator';
import FieldCommon from './FieldCommon';

export default {
  name: 'field-q-invoice-item',
  mixins: [abstractField, FieldCommon],
  components: { MagicMarkdown },

  setup() {
    const afd = MixinDataToReactiveData(abstractField);
    const fcd = MixinDataToReactiveData(FieldCommon);
    return {
      ...afd,
      ...fcd,
    };
  },

  computed: {
    // We just include the whole site that has currency information but also
    // specify that this price is a cent integer.
    priceFmtOpts() {
      return { ...this.site, isInteger: true };
    },

    units() {
      let minVal = this.invoiceItemOptions?.minUnits || 0;
      let maxVal = this.invoiceItemOptions?.maxUnits || 100;
      let step = this.invoiceItemOptions?.unitGranularity || 1;

      // Do some value sanity checks in case something snuck past the UI.
      if (!_.isInteger(minVal)) minVal = 0;
      if (!_.isInteger(maxVal)) maxVal = 100;
      if (minVal < 0) minVal = 0;
      if (minVal > maxVal) maxVal = minVal;
      if (!_.isInteger(step)) step = 1;
      if (step < 1) step = 1;

      // Return an inclusive range between [minVal, ..step.., maxVal].
      // range does not include the end so we add 1.
      return _.range(minVal, maxVal + 1, step);
    },

    useSwitchForUnits() {
      return this.invoiceItemOptions?.minUnits === 0 &&
        this.invoiceItemOptions?.maxUnits === 1 &&
        this.invoiceItemOptions?.unitGranularity === 1;
    },

    invoiceItemOptions() {
      return this.schema.invoiceItemOptions || {};
    },
  },

  mounted() {
    if (!this.value && !_.isNil(this.invoiceItemOptions?.minUnits)) {
      this.value = this.invoiceItemOptions.minUnits;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '~assets/css/colors';
.invoice-item {
  /* Give more room if we can. */
  display: flex;
  flex-gap: 1em;
  flex-direction: row;
  justify-content: flex-start;
  align-items: stretch;

  .image {
    order: 7;
    flex-basis: 130px;
    :deep(img) {
      max-width: 120px;
      max-height: 120px;
      border-radius: 1em;
      box-shadow: 3px 3px 3px rgba(0,0,0,0.2);
    }
  }
  .description {
    order: 1;
    flex-grow: 2;
  }
  .total {
    order: 3;
    flex-basis: 12em;
  }

  .unitsValue {
    font-weight: bold;
    font-size: 1.1em;
  }

  select.unitsValue {
    width: 4em;
  }

  .price {
    display: inline;
    font-size: larger;
    padding-right: 0.5em;
  }

  .times {
    display: inline;
    padding: 0 0.5em;
  }
  .units {
    display: inline;
    padding: 0 0.5em;
  }
}
</style>
